'use client';

import { Minus, Plus } from '@phosphor-icons/react/ssr';
import {
  type ComponentProps,
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { tv } from '../../lib/tv';
import { useFieldControl } from './field';

export type NumberStepperProps = Omit<
  ComponentProps<'input'>,
  'size' | 'value' | 'defaultValue' | 'onChange' | 'type' | 'min' | 'max' | 'step'
> & {
  /** Figma "Size": Small 112×32 · Medium 128×40 (default). */
  size?: 'sm' | 'md';
  value?: number | null;
  defaultValue?: number | null;
  /** Called with the new whole number, or `null` while the input is empty. */
  onValueChange?: (value: number | null) => void;
  min?: number;
  max?: number;
  /** Amount per −/+ press or ↑/↓ key. PageUp/PageDown step ×10. Default 1. */
  step?: number;
  /** Accessible names of the buttons. */
  decrementLabel?: string;
  incrementLabel?: string;
  /** Class for the outer box (width, margins). */
  className?: string;
};

/** Hold −/+ to repeat: first repeat after 400ms, then every 60ms. */
const REPEAT_DELAY = 400;
const REPEAT_INTERVAL = 60;

const stepper = tv({
  slots: {
    root: [
      'group/stepper bg-bg-surface border-border-strong text-text-primary relative inline-flex shrink-0 overflow-hidden rounded-md border',
      'transition-[border-color,box-shadow] duration-[120ms] ease-out',
      // The extra 0.5px of the 1.5px Focus / Error stroke is an inset ring on
      // an overlay: on the box itself the button fills would paint over it.
      "after:pointer-events-none after:absolute after:inset-0 after:rounded-[7px] after:content-['']",
      'has-[input:focus-visible]:border-border-focus has-[input:focus-visible]:shadow-focus-input',
      'has-[input:focus-visible]:after:inset-ring-[0.5px] has-[input:focus-visible]:after:inset-ring-border-focus',
      'has-[input[aria-invalid=true]]:border-status-danger-fg',
      'has-[input[aria-invalid=true]]:after:inset-ring-[0.5px] has-[input[aria-invalid=true]]:after:inset-ring-status-danger-fg',
      'has-[input:disabled]:bg-bg-disabled has-[input:disabled]:border-border-default',
    ],
    button: [
      'bg-bg-subtle border-border-default text-icon-primary flex shrink-0 items-center justify-center outline-none',
      'transition-colors duration-[120ms] ease-out enabled:hover:bg-bg-muted',
      // Disabled (the whole stepper, or one button at min / max): bg/disabled, icon/tertiary.
      'disabled:bg-bg-disabled disabled:text-icon-tertiary disabled:cursor-not-allowed',
    ],
    input: [
      'text-data-md min-w-0 flex-1 bg-transparent text-center tabular-nums outline-none',
      'aria-invalid:text-status-danger-fg disabled:text-text-disabled disabled:cursor-not-allowed',
    ],
  },
  variants: {
    size: {
      sm: { root: 'h-8 w-28', button: 'w-8 [&_svg]:size-3.5' },
      md: { root: 'h-10 w-32', button: 'w-10 [&_svg]:size-4' },
    },
  },
  defaultVariants: { size: 'md' },
});

const clamp = (n: number, min?: number, max?: number) =>
  Math.min(max ?? Infinity, Math.max(min ?? -Infinity, n));

/**
 * Small whole-number values with clear bounds - variation count, seats,
 * retries (Figma: Components ↳ Slider & Stepper ↳ Number Stepper). Type, or
 * use −/+ (hold to repeat); ↑/↓ step, PageUp/PageDown ×10, Home/End jump to
 * min/max. A `role="spinbutton"`; the buttons disable at the bounds and a
 * typed value outside min/max shows Error (say why in the Field's error, e.g.
 * “Max 12 variations”). Works inside Field.
 */
export function NumberStepper({
  size,
  value,
  defaultValue = null,
  onValueChange,
  min,
  max,
  step = 1,
  decrementLabel = 'Decrease',
  incrementLabel = 'Increase',
  className,
  onKeyDown,
  onBlur,
  ...props
}: NumberStepperProps) {
  const inputProps = useFieldControl(props) as typeof props & {
    'aria-invalid'?: boolean;
  };
  const [internal, setInternal] = useState<number | null>(defaultValue);
  const current = value === undefined ? internal : value;
  const [draft, setDraft] = useState<string | null>(null);
  const generatedId = useId();
  const inputId = inputProps.id ?? generatedId;
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  // The latest value, for hold-to-repeat ticks scheduled before a re-render.
  const latest = useRef(current);
  useEffect(() => {
    latest.current = current;
  }, [current]);

  const outOfRange = current !== null && clamp(current, min, max) !== current;
  const invalid = Boolean(inputProps['aria-invalid']) || outOfRange;
  const disabled = Boolean(inputProps.disabled);
  const styles = stepper({ size });

  const commit = (next: number | null) => {
    latest.current = next;
    setInternal(next);
    setDraft(null);
    onValueChange?.(next);
  };
  /** Step from the current value, into range. Returns false at a bound. */
  const stepBy = (delta: number) => {
    const from = latest.current ?? min ?? 0;
    const next = clamp(from + delta, min, max);
    if (next === latest.current) return false;
    commit(next);
    return true;
  };

  const stopRepeat = () => clearTimeout(timer.current);
  useEffect(() => stopRepeat, []);

  /** −/+ handlers; the button's `data-delta` says which way. */
  const deltaOf = (el: HTMLElement) => Number(el.dataset.delta);
  const onPressStart = (event: PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return;
    event.preventDefault(); // keep focus where it is
    stopRepeat();
    const delta = deltaOf(event.currentTarget);
    if (!stepBy(delta)) return;
    const repeat = (delay: number) => {
      timer.current = setTimeout(() => {
        if (stepBy(delta)) repeat(REPEAT_INTERVAL);
      }, delay);
    };
    repeat(REPEAT_DELAY);
  };
  // Keyboard and assistive-tech activation (no pointer): one step.
  const onActivate = (event: MouseEvent<HTMLButtonElement>) => {
    if (event.detail === 0) stepBy(deltaOf(event.currentTarget));
  };
  const press = {
    onPointerDown: onPressStart,
    onPointerUp: stopRepeat,
    onPointerLeave: stopRepeat,
    onPointerCancel: stopRepeat,
    onClick: onActivate,
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;
    const jumps: Record<string, () => void> = {
      ArrowUp: () => stepBy(step),
      ArrowDown: () => stepBy(-step),
      PageUp: () => stepBy(step * 10),
      PageDown: () => stepBy(-step * 10),
      Home: () => min !== undefined && commit(min),
      End: () => max !== undefined && commit(max),
    };
    const jump = jumps[event.key];
    if (!jump) return;
    event.preventDefault();
    jump();
  };

  return (
    <div data-slot="number-stepper" className={styles.root({ className })}>
      <button
        type="button"
        tabIndex={-1}
        aria-label={decrementLabel}
        aria-controls={inputId}
        disabled={disabled || (min !== undefined && current !== null && current <= min)}
        data-slot="number-stepper-decrement"
        className={styles.button({ className: 'border-r' })}
        data-delta={-step}
        {...press}
      >
        <Minus aria-hidden />
      </button>
      <input
        type="text"
        inputMode="numeric"
        role="spinbutton"
        autoComplete="off"
        aria-valuenow={current ?? undefined}
        aria-valuemin={min}
        aria-valuemax={max}
        {...inputProps}
        id={inputId}
        aria-invalid={invalid || undefined}
        value={draft ?? (current === null ? '' : String(current))}
        onChange={(event) => {
          const text = event.target.value.trim();
          if (!/^-?\d*$/.test(text)) return;
          setDraft(text);
          const parsed = text === '' || text === '-' ? null : Number(text);
          latest.current = parsed;
          setInternal(parsed);
          onValueChange?.(parsed);
        }}
        onKeyDown={handleKeyDown}
        onBlur={(event) => {
          setDraft(null);
          onBlur?.(event);
        }}
        data-slot="number-stepper-input"
        className={styles.input()}
      />
      <button
        type="button"
        tabIndex={-1}
        aria-label={incrementLabel}
        aria-controls={inputId}
        disabled={disabled || (max !== undefined && current !== null && current >= max)}
        data-slot="number-stepper-increment"
        className={styles.button({ className: 'border-l' })}
        data-delta={step}
        {...press}
      >
        <Plus aria-hidden />
      </button>
    </div>
  );
}
